import { cs } from '../../lib/currency';
import { RETIRED_TOOLS_VISIBLE } from '../../lib/retiredTools';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { FileText, Loader2, Receipt, Search, RefreshCw, AlertTriangle, ExternalLink, Plus } from 'lucide-react';
import api from '../../services/api';
import ReconcileByPIDialog from '../../components/admin/ReconcileByPIDialog';
import RefundDialog from '../../components/admin/RefundDialog';
import AdminPaymentNotifications from '../../components/admin/AdminPaymentNotifications';
import { toast } from 'sonner';

// Refactored modular pieces
import InvoiceStats from '../../components/admin/invoice/InvoiceStats';
import { DateRangePicker } from './billing/DateRangePicker';
import InvoiceFilters from '../../components/admin/invoice/InvoiceFilters';
import InvoiceTable from '../../components/admin/invoice/InvoiceTable';
import { sortInvoices } from '../../components/admin/invoice/sortInvoices';
import ChargeCardDialog from '../../components/admin/invoice/ChargeCardDialog';
import MarkPaidDialog from '../../components/admin/invoice/MarkPaidDialog';
import InvoiceViewDialog from '../../components/admin/invoice/InvoiceViewDialog';
import EditInvoiceDialog from '../../components/admin/invoice/EditInvoiceDialog';
import VoidInvoiceDialog from '../../components/admin/invoice/VoidInvoiceDialog';
import ReminderDialog from '../../components/admin/invoice/ReminderDialog';
import CombinedPaymentLinkDialog from '../../components/admin/invoice/CombinedPaymentLinkDialog';
import LaneDuplicatesDialog from '../../components/admin/invoice/LaneDuplicatesDialog';
import ManualInvoiceDialog from '../../components/admin/invoice/ManualInvoiceDialog';
import { invoiceKey, isReminderEligible } from '../../components/admin/invoice/utils.jsx';
import { useBusinessTimezone } from '../../utils/businessTime';

const InvoiceManagement = () => {
  const businessTz = useBusinessTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'overdue');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [methodFilter, setMethodFilter] = useState(searchParams.get('method') || 'all');
  // Duration filter — scopes the whole page (cards + list) to a period;
  // defaults to the current month so the ledger loads a small slice fast.
  const [dateRange, setDateRange] = useState({ period: 'current_month' });
  // Location filter — aligns with the Transactions page.
  const [locationFilter, setLocationFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  // iter382 — "Run Report" gating. The heavy filters (type, method,
  // location, period) are NOT queried on every change; they are committed
  // into `appliedFilters` only when the admin clicks "Run Report". The
  // status tab stays live (it's a view toggle). This stops a query storm
  // while the admin is still setting year/location/type.
  const [appliedFilters, setAppliedFilters] = useState({
    typeFilter: searchParams.get('type') || 'all',
    methodFilter: searchParams.get('method') || 'all',
    locationFilter: 'all',
    dateRange: { period: 'current_month' },
  });
  // iter321c — Accept a `customer` query param so the Transactions page
  // (and any other deep-link source) can land admins on a pre-filtered
  // invoice list for that customer. Free-text — matched against
  // parent_name / parent_email / description / player_names by the
  // existing local search predicate below.
  const [searchQuery, setSearchQuery] = useState(searchParams.get('customer') || '');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [reconcilingAll, setReconcilingAll] = useState(false);

  const fromSmallGroup = searchParams.get('type') === 'small_group';

  // Sorting
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Active dialog + invoice context
  const [chargeDialog, setChargeDialog] = useState(false);
  const [markPaidDialog, setMarkPaidDialog] = useState(false);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [voidDialog, setVoidDialog] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Bulk reminder
  const [selectedIds, setSelectedIds] = useState([]); // composite key `type:id`
  const [reminderDialog, setReminderDialog] = useState({ open: false, mode: 'bulk', single: null });

  // Combined payment-link dialog
  const [paymentLinkDialog, setPaymentLinkDialog] = useState(false);
  const [manualInvoiceDialog, setManualInvoiceDialog] = useState(false);
  // Lane duplicates dialog
  const [laneDupesDialog, setLaneDupesDialog] = useState(false);

  // ---- Data fetch ----------------------------------------------------------
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { typeFilter: t, methodFilter: m, locationFilter: loc, dateRange: dr } = appliedFilters;
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      if (t !== 'all') {
        params.append('invoice_type', t);
      }
      if (m && m !== 'all') {
        params.append('payment_method', m);
      }
      if (loc && loc !== 'all') {
        params.append('location_id', loc);
      }
      if (dr?.period === 'custom' && dr.startDate && dr.endDate) {
        params.append('start_date', dr.startDate);
        params.append('end_date', dr.endDate);
      } else if (dr?.period) {
        params.append('period', dr.period);
      }
      const res = await api.get(`/admin/invoices/all?${params.toString()}`);
      setInvoices(res.data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, appliedFilters]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // iter382 — commit the live filter selections and run the report.
  const runReport = useCallback(() => {
    setAppliedFilters({ typeFilter, methodFilter, locationFilter, dateRange });
  }, [typeFilter, methodFilter, locationFilter, dateRange]);

  // True when the live filters differ from what's currently applied —
  // used to highlight the "Run Report" button.
  const filtersDirty = useMemo(() => (
    appliedFilters.typeFilter !== typeFilter ||
    appliedFilters.methodFilter !== methodFilter ||
    appliedFilters.locationFilter !== locationFilter ||
    JSON.stringify(appliedFilters.dateRange) !== JSON.stringify(dateRange)
  ), [appliedFilters, typeFilter, methodFilter, locationFilter, dateRange]);

  useEffect(() => {
    api.get('/locations/active').then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);


  const [reconcileResult, setReconcileResult] = useState(null);
  const [healingForwards, setHealingForwards] = useState(false);

  const handleHealOrphanForwards = async () => {
    if (!window.confirm(
      'Scan paid Stripe payment_transactions whose linked invoice is still ' +
      'pending, and auto-flip them to paid?\n\n' +
      'Idempotent — only touches rows where the underlying invoice is ' +
      'actually still unpaid. Safe to re-run.'
    )) {
      return;
    }
    setHealingForwards(true);
    try {
      const { data } = await api.post('/admin/payments/heal-orphan-forwards');
      const {
        scanned = 0, healed = 0, skipped_no_link = 0,
        by_entity_type = {}, sample = [],
      } = data || {};
      if (healed > 0) {
        const breakdown = Object.entries(by_entity_type)
          .map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(' · ');
        toast.success(
          `Healed ${healed} orphan invoice${healed > 1 ? 's' : ''} (scanned ${scanned}). ${breakdown}`,
          { duration: 8000 },
        );
        // Log sample to console for follow-up.
        if (sample.length) {
          console.info('[heal-orphan-forwards] sample:', sample);
        }
      } else if (skipped_no_link > 0) {
        toast.warning(
          `Scanned ${scanned}, nothing to heal. ${skipped_no_link} txn(s) reference deleted entities.`,
        );
      } else {
        toast.info(`Scanned ${scanned} paid transactions — nothing to heal.`);
      }
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setHealingForwards(false);
    }
  };

  const handleReconcileAll = async () => {
    if (!window.confirm(
      'Scan EVERY parent for orphan Stripe payments and auto-link them to ' +
      'matching unpaid invoices (Small Group AND Enrollment)?\n\n' +
      'Safe: only exact amount + date matches are linked. ' +
      'Ambiguous rows are reported for manual review.'
    )) {
      return;
    }
    setReconcilingAll(true);
    try {
      // iter229: switched from the small-group-only endpoint to the
      // unified `/admin/reconcile-all-parents` which runs BOTH
      // reconcilers in a single pass.
      const { data } = await api.post('/admin/reconcile-all-parents');
      const {
        parents_inspected = 0,
        sg_matched = 0,
        sg_ambiguous = 0,
        enrollment_matched = 0,
        enrollment_ambiguous = 0,
      } = data || {};
      setReconcileResult(data);
      const matchedTotal = sg_matched + enrollment_matched;
      const ambigTotal = sg_ambiguous + enrollment_ambiguous;
      if (matchedTotal > 0) {
        const parts = [];
        if (sg_matched > 0) parts.push(`${sg_matched} small-group`);
        if (enrollment_matched > 0) parts.push(`${enrollment_matched} enrollment`);
        toast.success(
          `Reconciled ${matchedTotal} payment${matchedTotal > 1 ? 's' : ''} ` +
          `(${parts.join(' + ')}) across ${parents_inspected} parents` +
          (ambigTotal ? ` · ${ambigTotal} need review` : ''),
        );
      } else if (ambigTotal > 0) {
        toast.warning(`${ambigTotal} ambiguous match${ambigTotal > 1 ? 'es' : ''} — review below.`);
      } else {
        toast.info(`Nothing to reconcile (scanned ${parents_inspected} parents).`);
      }
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconcilingAll(false);
    }
  };

  // ---- Sorting + filtering -------------------------------------------------
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredInvoices = useMemo(() => {
    // iter307c — Use shared sortInvoices util. Handles null dates,
    // numeric coercion, and accessor mapping for parent/player/method.
    const matches = invoices.filter((inv) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        inv.parent_name?.toLowerCase().includes(q) ||
        inv.parent_email?.toLowerCase().includes(q) ||
        inv.description?.toLowerCase().includes(q) ||
        inv.player_names?.toLowerCase().includes(q)
      );
    });
    return sortInvoices(matches, sortColumn, sortDirection);
  }, [invoices, searchQuery, sortColumn, sortDirection]);

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ---- Selection -----------------------------------------------------------
  const eligiblePageInvoices = paginatedInvoices.filter(isReminderEligible);
  const eligibleFilteredInvoices = filteredInvoices.filter(isReminderEligible);
  const allPageSelected = eligiblePageInvoices.length > 0 &&
    eligiblePageInvoices.every((inv) => selectedIds.includes(invoiceKey(inv)));

  const toggleRow = (inv) => {
    const key = invoiceKey(inv);
    setSelectedIds((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const togglePage = () => {
    const pageKeys = eligiblePageInvoices.map(invoiceKey);
    setSelectedIds((prev) => allPageSelected
      ? prev.filter((k) => !pageKeys.includes(k))
      : Array.from(new Set([...prev, ...pageKeys])));
  };

  const selectAllMatchingFilter = () => {
    setSelectedIds(eligibleFilteredInvoices.map(invoiceKey));
  };

  const clearSelection = () => setSelectedIds([]);

  // Reset selection / page when filters change
  useEffect(() => { setSelectedIds([]); }, [statusFilter, typeFilter, searchQuery, itemsPerPage]);
  useEffect(() => { setCurrentPage(1); }, [statusFilter, typeFilter, searchQuery, sortColumn, sortDirection]);

  // ---- Bulk actions --------------------------------------------------------
  const bulkLaneEligible = useMemo(() => {
    if (selectedIds.length === 0) return { eligible: false, reason: 'Select at least one lane booking' };
    const rows = filteredInvoices.filter((inv) => selectedIds.includes(invoiceKey(inv)));
    if (rows.some((r) => r.type !== 'lane_booking')) return { eligible: false, reason: 'Combined payment link only supports lane bookings' };
    const emails = new Set(rows.map((r) => r.parent_email).filter(Boolean));
    if (emails.size > 1) return { eligible: false, reason: 'All bookings must belong to the same customer' };
    if (rows.some((r) => r.status === 'paid' || r.status === 'void')) return { eligible: false, reason: 'Selection includes paid/void bookings' };
    if (!rows.length) return { eligible: false, reason: 'No eligible bookings' };
    return { eligible: true, reason: '' };
  }, [filteredInvoices, selectedIds]);

  const selectedRows = useMemo(
    () => filteredInvoices.filter((inv) => selectedIds.includes(invoiceKey(inv))),
    [filteredInvoices, selectedIds],
  );

  const openBulkReminderDialog = () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one invoice');
      return;
    }
    setReminderDialog({ open: true, mode: 'bulk', single: null });
  };

  const openSingleReminderDialog = (invoice) => {
    setReminderDialog({ open: true, mode: 'single', single: invoice });
  };

  const openBulkPaymentLinkDialog = () => {
    if (!bulkLaneEligible.eligible) {
      toast.error(bulkLaneEligible.reason);
      return;
    }
    setPaymentLinkDialog(true);
  };

  // After a bulk-reminder run with failures, keep only the failed ids in
  // the selection so a retry only targets them.
  const handleRetainFailures = (failures) => {
    const failedKeys = new Set(failures.map((f) => `${f.type}:${f.id}`));
    setSelectedIds((prev) => prev.filter((k) => failedKeys.has(k)));
  };

  // ---- Row actions ---------------------------------------------------------
  const openView = (invoice) => { setSelectedInvoice(invoice); setViewDialog(true); };
  const openEdit = (invoice) => { setSelectedInvoice(invoice); setEditDialog(true); };
  const openCharge = (invoice) => { setSelectedInvoice(invoice); setChargeDialog(true); };
  const openMarkPaid = (invoice) => { setSelectedInvoice(invoice); setMarkPaidDialog(true); };
  const openVoid = (invoice) => { setSelectedInvoice(invoice); setVoidDialog(true); };

  // iter251: manual receipt resend — admin/staff can re-email the paid
  // invoice receipt to the parent/customer (helpful after webhook misses,
  // parent-side spam filtering, or offline payments recorded after the fact).
  const handleSendReceipt = async (invoice) => {
    const target = invoice.parent_email || invoice.email || invoice.parent_name || 'the parent';
    if (!window.confirm(
      `Send a paid-invoice receipt email for "${invoice.description || invoice.type}" (${cs()}${(invoice.amount || 0).toFixed(2)}) to ${target}?`
    )) return;
    try {
      const idForApi = invoice.type === 'small_group'
        ? String(invoice.id).replace(/^sg-/, '')
        : invoice.id;
      const { data } = await api.post('/admin/invoices/send-receipt', {
        invoice_type: invoice.type,
        invoice_id: idForApi,
      });
      toast.success(`Receipt emailed to ${data.sent_to} — ${cs()}${(data.amount || 0).toFixed(2)} (${data.payment_method || 'card'})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send receipt');
    }
  };
  const openRefund = (invoice) => { setSelectedInvoice(invoice); setRefundDialog(true); };
  // iter365 — mark a refunded / partially-refunded invoice fully paid so it
  // stops sending payment reminders (admin "close the loop" after a refund).
  const handleMarkSettled = async (invoice) => {
    const TYPE_MAP = {
      small_group: 'small_group_invoice',
      enrollment: 'enrollment',
      tournament: 'tournament_registration',
      lane_booking: 'lane_booking',
      manual: 'manual_invoice',
      uniform: 'manual_invoice',
    };
    const entityType = TYPE_MAP[invoice.type] || 'manual_invoice';
    const entityId = invoice.type === 'small_group'
      ? String(invoice.id).replace(/^sg-/, '')
      : invoice.id;
    if (!window.confirm(
      `Mark "${invoice.invoice_number || invoice.description || invoice.id}" fully paid and stop all payment reminders for it?`
    )) return;
    try {
      await api.post('/admin/invoices/mark-settled', {
        entity_type: entityType,
        entity_id: entityId,
        monthly_invoice_id: invoice.monthly_invoice_id || undefined,
      });
      toast.success('Invoice marked fully paid — reminders stopped');
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark as settled');
    }
  };
  // iter244: undo an accidental void — restores status back to "pending"
  // so the invoice is collectable again. Server validates that the row
  // is actually in a void state before flipping it.
  const handleUnvoid = async (invoice) => {
    if (!window.confirm(`Restore "${invoice.invoice_number || invoice.id}" back to pending?\n\nThe void reason will be cleared but kept on the audit trail (last_void_reason).`)) return;
    try {
      // iter306p: the list returns `type` (small_group|enrollment|tournament|
      // lane_booking|manual). The old code read `invoice_type` first and
      // defaulted to 'manual' — which made every non-manual invoice's
      // unvoid call hit the manual_invoices lookup branch and fail with
      // "Manual invoice not found". Prefer `type`, fall back to
      // `invoice_type` for legacy callers.
      const inv_type = invoice.type || invoice.invoice_type || 'manual';
      // small_group rows ship the prefixed `sg-{id}` — the server strips
      // it but for any future invoice_types with their own prefix this
      // keeps us safe.
      const idForApi = inv_type === 'small_group'
        ? String(invoice.id).replace(/^sg-/, '')
        : invoice.id;
      await api.post('/admin/invoices/unvoid', { invoice_id: idForApi, invoice_type: inv_type });
      toast.success('Invoice restored — back to pending');
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unvoid failed');
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 sm:p-6">
        {/* Header */}
        <div>
          {fromSmallGroup && (
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setTypeFilter('all'); setSearchParams({}); }}
                className="text-blue-600 hover:text-blue-700"
              >
                <Receipt className="h-4 w-4 mr-2" />
                View All Invoices
              </Button>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Invoice Management</h1>
              <p className="text-muted-foreground">
                {fromSmallGroup
                  ? 'Showing Small Group invoices - Click "View All Invoices" to see all types'
                  : 'Manage and collect payments for all invoice types'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <AdminPaymentNotifications variant="bell" />
              <Button
                variant="default"
                onClick={() => setManualInvoiceDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="create-manual-invoice-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
              {RETIRED_TOOLS_VISIBLE && (<Button
                variant="outline"
                onClick={() => setReconcileDialogOpen(true)}
                className="text-blue-700 border-blue-200 hover:bg-blue-50"
                data-testid="open-reconcile-by-pi"
                title="Mark a record paid using its Stripe PaymentIntent ID"
              >
                <Search className="h-4 w-4 mr-2" />
                Reconcile by Stripe PI
              </Button>)}
              <Button
                variant="outline"
                onClick={handleReconcileAll}
                disabled={reconcilingAll}
                className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                data-testid="reconcile-all-parents-btn"
                title="Scan every parent and auto-link orphan Stripe payments to matching unpaid Small Group AND Enrollment invoices"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${reconcilingAll ? 'animate-spin' : ''}`} />
                {reconcilingAll ? 'Reconciling…' : 'Reconcile All Parents'}
              </Button>
              <Button
                variant="outline"
                onClick={handleHealOrphanForwards}
                disabled={healingForwards}
                className="text-purple-700 border-purple-200 hover:bg-purple-50"
                data-testid="heal-orphan-forwards-btn"
                title="Find paid Stripe payment_transactions whose linked invoice is still pending and auto-flip them"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${healingForwards ? 'animate-spin' : ''}`} />
                {healingForwards ? 'Healing…' : 'Heal Orphan Forwards'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setLaneDupesDialog(true)}
                className="text-orange-700 border-orange-200 hover:bg-orange-50"
                data-testid="open-lane-duplicates-dialog"
                title="Find lane bookings that share customer + lane + date + start_time (likely duplicates from abandoned-retry checkouts)"
              >
                <FileText className="h-4 w-4 mr-2" />
                Find Lane Duplicates
              </Button>
            </div>
          </div>
        </div>

        {/* iter386 — unified filter bar: ALL filters live ABOVE the cards in
            one wrapping row (search, type, status, method, location, period,
            Run Report). Previously search/type/status/method sat below the
            cards in the table header, which split the controls across the page. */}
        <div className="space-y-2">
          <p className="text-sm text-slate-500">Showing invoices for the selected period</p>
          <div className="flex flex-wrap items-center gap-2">
            <InvoiceFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              typeFilter={typeFilter}
              onTypeFilterChange={setTypeFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              methodFilter={methodFilter}
              onMethodFilterChange={setMethodFilter}
            />
            {locations.length > 0 && (
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-[170px]" data-testid="invoice-location-filter">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="invoice-location-option-all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id} data-testid={`invoice-location-option-${loc.id}`}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <Button
              onClick={runReport}
              disabled={loading}
              className={filtersDirty ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
              variant={filtersDirty ? 'default' : 'outline'}
              data-testid="invoice-run-report-btn"
              title="Apply the selected type, location, method and period filters"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Run Report
            </Button>
          </div>
          {filtersDirty && (
            <p className="text-xs text-emerald-700" data-testid="invoice-filters-dirty-hint">
              Filters changed — click “Run Report” to update the cards and list.
            </p>
          )}
        </div>

        {/* KPIs deliberately derive from the exact final row set: selected report
            filters, live status filter, and local search all reconcile. */}
        <InvoiceStats invoices={filteredInvoices} loading={loading} />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              All Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InvoiceTable
              paginatedInvoices={paginatedInvoices}
              filteredInvoices={filteredInvoices}
              loading={loading}
              onSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              selectedIds={selectedIds}
              eligiblePageInvoices={eligiblePageInvoices}
              eligibleFilteredInvoices={eligibleFilteredInvoices}
              allPageSelected={allPageSelected}
              onTogglePage={togglePage}
              onToggleRow={toggleRow}
              onSelectAllMatching={selectAllMatchingFilter}
              onClearSelection={clearSelection}
              bulkLaneEligible={bulkLaneEligible}
              onOpenBulkPaymentLink={openBulkPaymentLinkDialog}
              onOpenBulkReminder={openBulkReminderDialog}
              onView={openView}
              onEdit={openEdit}
              onSendReminder={openSingleReminderDialog}
              onCharge={openCharge}
              onMarkPaid={openMarkPaid}
              onVoid={openVoid}
              onUnvoid={handleUnvoid}
              onRefund={openRefund}
              onSendReceipt={handleSendReceipt}
              onMarkSettled={handleMarkSettled}
              onSyncUpdated={fetchInvoices}
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
              onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              businessTz={businessTz}
            />
          </CardContent>
        </Card>

        <ChargeCardDialog
          open={chargeDialog}
          onOpenChange={setChargeDialog}
          invoice={selectedInvoice}
          onCharged={fetchInvoices}
        />

        <MarkPaidDialog
          open={markPaidDialog}
          onOpenChange={setMarkPaidDialog}
          invoice={selectedInvoice}
          onMarked={fetchInvoices}
        />

        <InvoiceViewDialog
          open={viewDialog}
          onOpenChange={setViewDialog}
          invoice={selectedInvoice}
          onCharge={openCharge}
          onMarkPaid={openMarkPaid}
          onMarkSettled={handleMarkSettled}
        />

        <EditInvoiceDialog
          open={editDialog}
          onOpenChange={setEditDialog}
          invoice={selectedInvoice}
          onEdited={fetchInvoices}
        />

        <VoidInvoiceDialog
          open={voidDialog}
          onOpenChange={setVoidDialog}
          invoice={selectedInvoice}
          onVoided={fetchInvoices}
        />

        <ReconcileByPIDialog
          open={reconcileDialogOpen}
          onOpenChange={setReconcileDialogOpen}
          onSuccess={fetchInvoices}
        />

        <ReminderDialog
          open={reminderDialog.open}
          onOpenChange={(open) => setReminderDialog((d) => ({ ...d, open }))}
          mode={reminderDialog.mode}
          single={reminderDialog.single}
          bulkInvoices={selectedRows}
          onSent={() => { if (reminderDialog.mode === 'bulk') clearSelection(); }}
          onRetainFailures={handleRetainFailures}
        />

        <CombinedPaymentLinkDialog
          open={paymentLinkDialog}
          onOpenChange={setPaymentLinkDialog}
          bookings={selectedRows}
          onDone={() => { clearSelection(); fetchInvoices(); }}
        />

        <LaneDuplicatesDialog
          open={laneDupesDialog}
          onOpenChange={setLaneDupesDialog}
          onCleanupComplete={fetchInvoices}
        />

        <ManualInvoiceDialog
          open={manualInvoiceDialog}
          onOpenChange={setManualInvoiceDialog}
          onCreated={fetchInvoices}
        />

        {/* iter223: Refund directly from the invoice row. */}
        {selectedInvoice && (() => {
          const TYPE_MAP = {
            small_group: 'small_group_invoice',
            enrollment: 'enrollment',
            tournament: 'tournament_registration',
            lane_booking: 'lane_booking',
            manual: 'manual_invoice',
          };
          const entityType = TYPE_MAP[selectedInvoice.type];
          // Small group invoices use the `sg-` prefix in the listing — strip it
          // so the refund service finds the row by its real `id`.
          const entityId = selectedInvoice.type === 'small_group'
            ? String(selectedInvoice.id).replace(/^sg-/, '')
            : selectedInvoice.id;
          // Build a thin `entity` shape RefundDialog can render.
          // iter310 — Prefer amount_charged when it exists (that's the
          // exact $ debited to the customer's card, already inclusive
          // of the CC surcharge). Only add ``surcharge_amount`` when
          // falling back to the invoice face amount, otherwise the
          // dialog would double-count the fee.
          const chargedAmt = Number(selectedInvoice.amount_charged);
          const useCharged = Number.isFinite(chargedAmt) && chargedAmt > 0;
          const entity = {
            id: entityId,
            price: useCharged ? chargedAmt : selectedInvoice.amount,
            refunded_amount: selectedInvoice.refunded_amount || 0,
            customer_name: selectedInvoice.parent_name,
            payment_method: selectedInvoice.payment_method,
            // Scope the refund breakdown to THIS specific monthly invoice
            // (enrollment invoices), so by-method resolves the right month
            // instead of the whole enrollment entity.
            monthly_invoice_id: selectedInvoice.monthly_invoice_id || undefined,
            // iter305: pass through the CC surcharge so the dialog's
            // "Amount paid" + Max refundable reflect what the customer
            // was actually debited (base + CC fee). Skip when we've
            // already used amount_charged (that includes the fee).
            surcharge_amount: useCharged ? 0 : selectedInvoice.surcharge_amount,
          };
          const summary = [
            { label: 'Customer', value: selectedInvoice.parent_name || '—' },
            { label: 'Description', value: selectedInvoice.description || '—' },
            { label: 'Type', value: selectedInvoice.type_label || selectedInvoice.type },
          ];
          return (
            <RefundDialog
              open={refundDialog}
              onOpenChange={setRefundDialog}
              entityType={entityType}
              entity={entity}
              priceField="price"
              summaryLines={summary}
              allowCancelCheckbox={false}
              onRefunded={() => { setRefundDialog(false); fetchInvoices(); }}
            />
          );
        })()}

        {/* Bulk reconcile result dialog — opens automatically after the
            "Reconcile All Parents" button runs. Lets admins jump to any
            parent who had matches or ambiguous rows for manual review. */}
        <Dialog
          open={!!reconcileResult}
          onOpenChange={(o) => { if (!o) setReconcileResult(null); }}
        >
          <DialogContent
            className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
            data-testid="reconcile-all-result-dialog"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-emerald-600" />
                Reconcile Results
              </DialogTitle>
              <DialogDescription>
                Scanned {reconcileResult?.parents_scanned || 0} parents · {' '}
                <span className="text-emerald-700 font-medium">
                  {reconcileResult?.matched || 0} matched
                </span>
                {' · '}
                <span className="text-amber-700 font-medium">
                  {reconcileResult?.ambiguous || 0} need review
                </span>
                {' · '}
                <span className="text-slate-500">
                  {reconcileResult?.no_match || 0} no match
                </span>
              </DialogDescription>
            </DialogHeader>

            <div className="overflow-y-auto flex-1 -mx-6 px-6">
              {(reconcileResult?.parents_with_changes || []).length === 0 ? (
                <p className="text-center text-sm text-slate-500 py-8">
                  No matches or ambiguous rows found. All parents are clean.
                </p>
              ) : (
                <div className="space-y-2">
                  {reconcileResult.parents_with_changes.map((p) => (
                    <div
                      key={p.parent_id}
                      className="flex items-center justify-between gap-3 border rounded-lg p-3 hover:bg-slate-50"
                      data-testid={`reconcile-row-${p.parent_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900 truncate">
                          {p.parent_name || '(unknown parent)'}
                        </div>
                        <div className="flex items-center gap-3 text-xs mt-1 flex-wrap">
                          {p.matched > 0 && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                              {p.matched} matched
                            </span>
                          )}
                          {p.ambiguous > 0 && (
                            <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                              <AlertTriangle className="h-3 w-3" />
                              {p.ambiguous} need review
                            </span>
                          )}
                          {p.no_match > 0 && (
                            <span className="text-slate-500">
                              {p.no_match} no match
                            </span>
                          )}
                          <span className="text-slate-400">
                            · {p.scanned_txns} scanned
                          </span>
                        </div>
                      </div>
                      <Link
                        to={`/admin/parents/${p.parent_id}?tab=billing`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline flex-shrink-0"
                        data-testid={`open-parent-${p.parent_id}`}
                      >
                        Open
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(reconcileResult?.ambiguous || 0) > 0 && (
              <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
                <strong className="text-amber-700">Why "ambiguous"?</strong> Two
                or more unpaid Small Group invoices for the same parent share
                the same amount, so the scan can't be sure which one the
                payment belongs to. Open the parent → Billing tab and use the
                "Reconcile by Stripe PI" tool to link it manually.
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setReconcileResult(null)}
                data-testid="close-reconcile-result"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default InvoiceManagement;
